'use client';

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface AuthScreenProps {
  onAuthSuccess: () => void;
}

type AuthMode = 'login' | 'signup' | 'forgot';

interface FormState {
  email: string;
  password: string;
  confirmPassword: string;
  displayName: string;
  username: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  displayName?: string;
  username?: string;
}

export default function AuthScreen({ onAuthSuccess }: AuthScreenProps) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [form, setForm] = useState<FormState>({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    username: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isDark] = useState(true);
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailError, setForgotEmailError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setGoogleError(null);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setGoogleError(err?.message || 'Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (!form.password) {
      newErrors.password = 'Password is required';
    } else if (form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (mode === 'signup') {
      if (!form.displayName.trim()) {
        newErrors.displayName = 'Display name is required';
      }
      if (!form.username.trim()) {
        newErrors.username = 'Username is required';
      } else if (!/^[a-zA-Z0-9_]+$/.test(form.username.trim())) {
        newErrors.username = 'Username can only contain letters, numbers, and underscores';
      }
      if (!form.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (form.password !== form.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setAuthError(null);
    try {
      if (mode === 'login') {
        await signIn(form.email, form.password);
      } else {
        await signUp(form.email, form.password, {
          fullName: form.displayName,
        });
      }
      onAuthSuccess();
    } catch (err: any) {
      setAuthError(err?.message || 'Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setErrors({});
    setForm({ email: '', password: '', confirmPassword: '', displayName: '', username: '' });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setForgotEmail('');
    setForgotEmailError('');
    setForgotSuccess(false);
    setForgotError(null);
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotEmailError('');
    setForgotError(null);
    if (!forgotEmail.trim()) {
      setForgotEmailError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      setForgotEmailError('Enter a valid email address');
      return;
    }
    setForgotLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setForgotSuccess(true);
    } catch (err: any) {
      setForgotError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-6 sm:py-10"
      style={{ background: 'var(--background)' }}
    >
      {/* Subtle background glow */}
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="absolute top-[-10%] left-[50%] translate-x-[-50%] w-[600px] h-[400px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(ellipse, #2EA6FF 0%, #6650D8 60%, transparent 100%)' }}
        />
        <div
          className="absolute bottom-[-5%] right-[-5%] w-[300px] h-[300px] rounded-full opacity-10 blur-3xl"
          style={{ background: 'radial-gradient(ellipse, #287FD8 0%, transparent 70%)' }}
        />
      </div>

      <div className="relative w-full max-w-md animate-fade-in-up">
        {/* Logo / Brand */}
        <div className="flex flex-col items-center mb-5 sm:mb-8">
          <div
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center mb-3 sm:mb-4 shadow-lg overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)' }}
          >
            <img
              src="/assets/images/image-1788059339670.png"
              alt="ChatNova Logo"
              width={56}
              height={56}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <h1
            className="text-xl sm:text-2xl font-bold tracking-tight"
            style={{ color: 'var(--foreground)' }}
          >
            Elbanrac
          </h1>
          {(mode === 'signup' || mode === 'forgot') && (
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
              {mode === 'signup' ? 'Create your account' : 'Reset your password'}
            </p>
          )}
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-5 sm:p-8 shadow-2xl"
          style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
          }}
        >
          {/* Forgot Password View */}
          {mode === 'forgot' ? (
            <div>
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="flex items-center gap-1.5 text-xs font-medium mb-6 transition-opacity hover:opacity-80"
                style={{ color: 'var(--muted-foreground)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 5l-7 7 7 7"/>
                </svg>
                Back to Sign In
              </button>

              {forgotSuccess ? (
                <div className="flex flex-col items-center text-center gap-4 py-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(74,222,128,0.15)' }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-base mb-1" style={{ color: 'var(--foreground)' }}>Check your email</p>
                    <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      We sent a password reset link to <span className="font-medium" style={{ color: 'var(--foreground)' }}>{forgotEmail}</span>. Check your inbox and follow the link to reset your password.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 mt-2"
                    style={{
                      background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
                      color: '#fff',
                      boxShadow: '0 4px 20px rgba(46,166,255,0.3)',
                    }}
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit} noValidate>
                  <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                    Enter your email address and we&apos;ll send you a link to reset your password.
                  </p>
                  <div className="flex flex-col gap-5">
                    <div>
                      <label
                        htmlFor="forgotEmail"
                        className="block text-xs font-semibold mb-1.5 tracking-wide uppercase"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        Email
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="4" width="20" height="16" rx="2"/>
                            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                          </svg>
                        </span>
                        <input
                          id="forgotEmail"
                          type="email"
                          autoComplete="email"
                          placeholder="you@example.com"
                          value={forgotEmail}
                          onChange={e => {
                            setForgotEmail(e.target.value);
                            if (forgotEmailError) setForgotEmailError('');
                          }}
                          className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                          style={{
                            background: 'var(--input)',
                            color: 'var(--foreground)',
                            border: forgotEmailError ? '1.5px solid #f87171' : '1.5px solid var(--border)',
                            boxShadow: forgotEmailError ? '0 0 0 3px rgba(248,113,113,0.12)' : 'none',
                          }}
                          onFocus={e => {
                            if (!forgotEmailError) {
                              e.currentTarget.style.border = '1.5px solid var(--primary)';
                              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46,166,255,0.15)';
                            }
                          }}
                          onBlur={e => {
                            if (!forgotEmailError) {
                              e.currentTarget.style.border = '1.5px solid var(--border)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        />
                      </div>
                      {forgotEmailError && (
                        <p className="mt-1.5 text-xs flex items-center gap-1" style={{ color: '#f87171' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                          {forgotEmailError}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200"
                      style={{
                        background: forgotLoading
                          ? 'var(--secondary)'
                          : 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
                        color: '#fff',
                        boxShadow: forgotLoading ? 'none' : '0 4px 20px rgba(46,166,255,0.3)',
                        cursor: forgotLoading ? 'not-allowed' : 'pointer',
                        opacity: forgotLoading ? 0.8 : 1,
                      }}
                    >
                      {forgotLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round"/>
                          </svg>
                          Sending…
                        </span>
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>

                    {forgotError && (
                      <p className="text-xs text-center flex items-center justify-center gap-1.5" style={{ color: '#f87171' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                        </svg>
                        {forgotError}
                      </p>
                    )}
                  </div>
                </form>
              )}
            </div>
          ) : (
            <>
              {/* Tab switcher */}
              <div
                className="flex rounded-xl p-1 mb-7"
                style={{ background: 'var(--background)' }}
              >
                {(['login', 'signup'] as AuthMode[]).map(tab => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => switchMode(tab)}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
                    style={{
                      background: mode === tab ? 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)' : 'transparent',
                      color: mode === tab ? '#fff' : 'var(--muted-foreground)',
                      boxShadow: mode === tab ? '0 2px 12px rgba(46,166,255,0.25)' : 'none',
                    }}
                  >
                    {tab === 'login' ? 'Sign In' : 'Sign Up'}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="flex flex-col gap-5">

                  {/* Display Name (signup only) */}
                  {mode === 'signup' && (
                    <div className="animate-fade-in">
                      <label
                        htmlFor="displayName"
                        className="block text-xs font-semibold mb-1.5 tracking-wide uppercase"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        Display Name
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                          </svg>
                        </span>
                        <input
                          id="displayName"
                          type="text"
                          autoComplete="name"
                          placeholder="Your name"
                          value={form.displayName}
                          onChange={handleChange('displayName')}
                          className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                          style={{
                            background: 'var(--input)',
                            color: 'var(--foreground)',
                            border: errors.displayName ? '1.5px solid #f87171' : '1.5px solid var(--border)',
                            boxShadow: errors.displayName ? '0 0 0 3px rgba(248,113,113,0.12)' : 'none',
                          }}
                          onFocus={e => {
                            if (!errors.displayName) {
                              e.currentTarget.style.border = '1.5px solid var(--primary)';
                              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46,166,255,0.15)';
                            }
                          }}
                          onBlur={e => {
                            if (!errors.displayName) {
                              e.currentTarget.style.border = '1.5px solid var(--border)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        />
                      </div>
                      {errors.displayName && (
                        <p className="mt-1.5 text-xs flex items-center gap-1" style={{ color: '#f87171' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                          {errors.displayName}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Username (signup only) */}
                  {mode === 'signup' && (
                    <div className="animate-fade-in">
                      <label
                        htmlFor="username"
                        className="block text-xs font-semibold mb-1.5 tracking-wide uppercase"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        Username
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="8" r="4"/>
                            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                          </svg>
                        </span>
                        <input
                          id="username"
                          type="text"
                          autoComplete="username"
                          placeholder="your_username"
                          value={form.username}
                          onChange={handleChange('username')}
                          className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                          style={{
                            background: 'var(--input)',
                            color: 'var(--foreground)',
                            border: errors.username ? '1.5px solid #f87171' : '1.5px solid var(--border)',
                            boxShadow: errors.username ? '0 0 0 3px rgba(248,113,113,0.12)' : 'none',
                          }}
                          onFocus={e => {
                            if (!errors.username) {
                              e.currentTarget.style.border = '1.5px solid var(--primary)';
                              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46,166,255,0.15)';
                            }
                          }}
                          onBlur={e => {
                            if (!errors.username) {
                              e.currentTarget.style.border = '1.5px solid var(--border)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        />
                      </div>
                      {errors.username && (
                        <p className="mt-1.5 text-xs flex items-center gap-1" style={{ color: '#f87171' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                          {errors.username}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-semibold mb-1.5 tracking-wide uppercase"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      Email
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="4" width="20" height="16" rx="2"/>
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                        </svg>
                      </span>
                      <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={handleChange('email')}
                        className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                        style={{
                          background: 'var(--input)',
                          color: 'var(--foreground)',
                          border: errors.email ? '1.5px solid #f87171' : '1.5px solid var(--border)',
                          boxShadow: errors.email ? '0 0 0 3px rgba(248,113,113,0.12)' : 'none',
                        }}
                        onFocus={e => {
                          if (!errors.email) {
                            e.currentTarget.style.border = '1.5px solid var(--primary)';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46,166,255,0.15)';
                          }
                        }}
                        onBlur={e => {
                          if (!errors.email) {
                            e.currentTarget.style.border = '1.5px solid var(--border)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1.5 text-xs flex items-center gap-1" style={{ color: '#f87171' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Password */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label
                        htmlFor="password"
                        className="block text-xs font-semibold tracking-wide uppercase"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        Password
                      </label>
                      {mode === 'login' && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => switchMode('forgot')}
                            className="text-xs font-medium transition-opacity hover:opacity-80"
                            style={{ color: 'var(--primary)' }}
                          >
                            Forgot password?
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                      </span>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                        placeholder="••••••••"
                        value={form.password}
                        onChange={handleChange('password')}
                        className="w-full pl-10 pr-11 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                        style={{
                          background: 'var(--input)',
                          color: 'var(--foreground)',
                          border: errors.password ? '1.5px solid #f87171' : '1.5px solid var(--border)',
                          boxShadow: errors.password ? '0 0 0 3px rgba(248,113,113,0.12)' : 'none',
                        }}
                        onFocus={e => {
                          if (!errors.password) {
                            e.currentTarget.style.border = '1.5px solid var(--primary)';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46,166,255,0.15)';
                          }
                        }}
                        onBlur={e => {
                          if (!errors.password) {
                            e.currentTarget.style.border = '1.5px solid var(--border)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80"
                        style={{ color: 'var(--muted-foreground)' }}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1.5 text-xs flex items-center gap-1" style={{ color: '#f87171' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password (signup only) */}
                  {mode === 'signup' && (
                    <div className="animate-fade-in">
                      <label
                        htmlFor="confirmPassword"
                        className="block text-xs font-semibold mb-1.5 tracking-wide uppercase"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        Confirm Password
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                          </svg>
                        </span>
                        <input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder="••••••••"
                          value={form.confirmPassword}
                          onChange={handleChange('confirmPassword')}
                          className="w-full pl-10 pr-11 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                          style={{
                            background: 'var(--input)',
                            color: 'var(--foreground)',
                            border: errors.confirmPassword ? '1.5px solid #f87171' : '1.5px solid var(--border)',
                            boxShadow: errors.confirmPassword ? '0 0 0 3px rgba(248,113,113,0.12)' : 'none',
                          }}
                          onFocus={e => {
                            if (!errors.confirmPassword) {
                              e.currentTarget.style.border = '1.5px solid var(--primary)';
                              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46,166,255,0.15)';
                            }
                          }}
                          onBlur={e => {
                            if (!errors.confirmPassword) {
                              e.currentTarget.style.border = '1.5px solid var(--border)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80"
                          style={{ color: 'var(--muted-foreground)' }}
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          {showConfirmPassword ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                              <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="mt-1.5 text-xs flex items-center gap-1" style={{ color: '#f87171' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                          {errors.confirmPassword}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Password strength hint (signup only) */}
                  {mode === 'signup' && form.password.length > 0 && (
                    <div className="animate-fade-in">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4].map(level => {
                          const strength = Math.min(
                            (form.password.length >= 8 ? 1 : 0) +
                            (/[A-Z]/.test(form.password) ? 1 : 0) +
                            (/[0-9]/.test(form.password) ? 1 : 0) +
                            (/[^A-Za-z0-9]/.test(form.password) ? 1 : 0),
                            4
                          );
                          const colors = ['#f87171', '#fb923c', '#facc15', '#4ade80'];
                          return (
                            <div
                              key={level}
                              className="h-1 flex-1 rounded-full transition-all duration-300"
                              style={{
                                background: level <= strength ? colors[strength - 1] : 'var(--border)',
                              }}
                            />
                          );
                        })}
                      </div>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        Use 8+ chars, uppercase, numbers & symbols for a strong password
                      </p>
                    </div>
                  )}

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 mt-1 relative overflow-hidden"
                    style={{
                      background: isLoading
                        ? 'var(--secondary)'
                        : 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
                      color: '#fff',
                      boxShadow: isLoading ? 'none' : '0 4px 20px rgba(46,166,255,0.3)',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.8 : 1,
                    }}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round"/>
                        </svg>
                        {mode === 'login' ? 'Signing in…' : 'Creating account…'}
                      </span>
                    ) : (
                      mode === 'login' ? 'Sign In' : 'Create Account'
                    )}
                  </button>

                  {/* Auth error message */}
                  {authError && (
                    <p className="text-xs text-center flex items-center justify-center gap-1.5 mt-1" style={{ color: '#f87171' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                      </svg>
                      {authError}
                    </p>
                  )}
                </div>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>or</span>
                <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
              </div>

              {/* Google Sign In */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || isLoading}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-semibold transition-all duration-200 mb-5"
                style={{
                  background: 'var(--input)',
                  color: 'var(--foreground)',
                  border: '1.5px solid var(--border)',
                  cursor: (googleLoading || isLoading) ? 'not-allowed' : 'pointer',
                  opacity: (googleLoading || isLoading) ? 0.7 : 1,
                }}
              >
                {googleLoading ? (
                  <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                )}
                {googleLoading ? 'Connecting…' : 'Continue with Google'}
              </button>

              {googleError && (
                <p className="text-xs text-center flex items-center justify-center gap-1.5 -mt-3 mb-4" style={{ color: '#f87171' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  {googleError}
                </p>
              )}

              {/* Switch mode link */}
              <p className="text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                <button
                  type="button"
                  onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')}
                  className="font-semibold transition-opacity hover:opacity-80"
                  style={{ color: 'var(--primary)' }}
                >
                  {mode === 'login' ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </>
          )}
        </div>

        {/* Footer note */}
        <p className="text-center text-xs mt-6" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
